import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import type { DiscordAuth } from '../../types';

interface AuthState {
  isReady: boolean;
  isAuthenticated: boolean;
  isInitializing: boolean;
  initError: string | null;
  hasAccess: boolean;
  auth: DiscordAuth | null;
}

const initialState: AuthState = {
  isReady: false,
  isAuthenticated: false,
  isInitializing: true,
  initError: null,
  hasAccess: true,
  auth: null,
};

export const { actions, reducer } = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setInitializing: (state, action: PayloadAction<boolean>) => {
      state.isInitializing = action.payload;
      if (action.payload) {
        state.initError = null;
      }
    },
    setReady: (state) => {
      state.isReady = true;
      state.isInitializing = false;
    },
    setInitError: (state, action: PayloadAction<string>) => {
      state.initError = action.payload;
      state.isInitializing = false;
    },
    setAuth: (state, action: PayloadAction<DiscordAuth>) => {
      state.auth = action.payload;
      state.isAuthenticated = true;
      state.isInitializing = false;
    },
    setHasAccess: (state, action: PayloadAction<boolean>) => {
      state.hasAccess = action.payload;
      state.isInitializing = false;
    },
    clearAuth: (state) => {
      state.auth = null;
      state.isAuthenticated = false;
    },
  },
});

// Define action interfaces for the custom hook
interface AuthActions {
  setInitializing: (isInitializing: boolean) => void;
  setReady: () => void;
  setInitError: (error: string) => void;
  setAuth: (auth: DiscordAuth) => void;
  setHasAccess: (hasAccess: boolean) => void;
  clearAuth: () => void;
}

// Combined type for the custom hook
type AuthSlice = AuthState & AuthActions;

// Custom hook that combines state and actions
export const useAuthSlice = (): AuthSlice => {
  const dispatch = useDispatch();
  return {
    // State selectors
    isReady: useSelector((state: RootState) => state.auth.isReady),
    isAuthenticated: useSelector((state: RootState) => state.auth.isAuthenticated),
    isInitializing: useSelector((state: RootState) => state.auth.isInitializing),
    initError: useSelector((state: RootState) => state.auth.initError),
    hasAccess: useSelector((state: RootState) => state.auth.hasAccess),
    auth: useSelector((state: RootState) => state.auth.auth),

    // Action dispatchers
    setInitializing: (isInitializing) => dispatch(actions.setInitializing(isInitializing)),
    setReady: () => dispatch(actions.setReady()),
    setInitError: (error) => dispatch(actions.setInitError(error)),
    setAuth: (auth) => dispatch(actions.setAuth(auth)),
    setHasAccess: (hasAccess) => dispatch(actions.setHasAccess(hasAccess)),
    clearAuth: () => dispatch(actions.clearAuth()),
  };
};

export default reducer;
